import { useFormikContext } from "formik";

import { FieldLabel, FieldSelect, FieldSwitch, FormRowField } from "@skilitics/designer-ui-components";
import { booleanOperators } from "@skilitics/judy";
import { useFormatMessage } from "@skilitics/localization";

import { useTriggerScopeNodeId } from "../../../triggers/HandlerDesignerContext.ts";
import type { ComparisonViewProps, ComparisonFieldValues } from "../../valueSubtypeDefinition.ts";
import { ValueConfigEditor } from "../ValueConfigEditor.tsx";
import { useOperatorOptions } from "./useOperatorOptions.ts";

/** The operator and operand rows of a boolean comparison. Rows only — a Form mounts it. */
export const BooleanComparisonView = ({
  scopeNodeId,
  excludeNodeId,
  bindable,
  disabled = false,
}: ComparisonViewProps) => {
  const formatMessage = useFormatMessage(booleanComparisonFormatStrings);
  const triggerScopeNodeId = useTriggerScopeNodeId();
  const { values } = useFormikContext<ComparisonFieldValues>();
  const operatorOptions = useOperatorOptions(booleanOperators, values.operator);

  return (
    <>
      <FormRowField<ComparisonFieldValues> name="operator">
        <FieldLabel />
        <FieldSelect disabled={disabled} options={operatorOptions} placeholder={formatMessage("operatorPlaceholder")} />
      </FormRowField>

      <FormRowField<ComparisonFieldValues> name="value">
        <FieldLabel />
        <ValueConfigEditor
          type="boolean"
          scopeNodeId={scopeNodeId ?? triggerScopeNodeId}
          excludeNodeId={excludeNodeId}
          bindable={bindable}
          disabled={disabled}
        >
          <FieldSwitch disabled={disabled} />
        </ValueConfigEditor>
      </FormRowField>
    </>
  );
};

/** Spread into the mounting form's `formatStrings` — an implicit `FieldLabel` reads its text from there. */
export const booleanComparisonFormatStrings = {
  operatorLabel: {
    en: "Operator",
  },
  operatorPlaceholder: {
    en: "Select an operator",
  },
  valueLabel: {
    en: "Value",
  },
};
